import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Site } from '~/types/locations'
import type { AssetStack } from '~/types/asset'
import type { Route } from '~/types/routes'

export type ViewMode = 'data' | 'list'
export type ListHeightLevel = 'full' | 'half' | 'none'
export type FloatingCardMode = 'default' | 'site' | 'distribution' | 'route'

export interface InterfaceStore {
  // Refs
  isLoading: Ref<boolean>
  viewMode: Ref<ViewMode>
  floatingCardMode: Ref<FloatingCardMode>
  isMapFocused: Ref<boolean>
  listHeightLevel: Ref<ListHeightLevel>
  isSearchBarVisible: Ref<boolean>
  openedSite: Ref<Site | null>
  openedRoute: Ref<Route | null>
  openedStack: Ref<AssetStack | null>
  stackType: Ref<string | null>
  showAllTelemetries: Ref<boolean>
  telemetrySearchQuery: Ref<string>
  actionSearchQuery: Ref<string>
  configurationSearchQuery: Ref<string>
  configurationOpenActionKey: Ref<string | null>

  // Actions
  setViewMode: (mode: ViewMode) => void
  setFloatingCardMode: (mode: FloatingCardMode) => void
  setOpenedStack: (type: string, stack: AssetStack) => void
  resetSearchQueries: () => void
  resetAssetPanelState: () => void

  $reset: () => void
}

export const useInterfaceStore = defineStore('interfaceStore', (): InterfaceStore => {
  // State
  const isLoading = ref<boolean>(false)
  const viewMode = ref<ViewMode>('data' as ViewMode)
  const floatingCardMode = ref<FloatingCardMode>('default')
  const isMapFocused = ref<boolean>(false)
  const listHeightLevel = ref<ListHeightLevel>('half' as ListHeightLevel)
  const isSearchBarVisible = ref<boolean>(false)
  const openedSite = ref<Site | null>(null) // Temporary. Thinking of replacing this with a routing system
  const openedRoute = ref<Route | null>(null)
  const openedStack = ref<AssetStack | null>(null)
  const stackType = ref<string | null>(null)
  const showAllTelemetries = ref<boolean>(false)
  const telemetrySearchQuery = ref<string>('')
  const actionSearchQuery = ref<string>('')
  const configurationSearchQuery = ref<string>('')
  const configurationOpenActionKey = ref<string | null>(null)

  // Actions
  const setViewMode = (mode: ViewMode): void => {
    viewMode.value = mode
  }

  const setFloatingCardMode = (mode: FloatingCardMode): void => {
    floatingCardMode.value = mode
  }

  const setOpenedStack = (type: string, stack: AssetStack): void => {
    stackType.value = type
    openedStack.value = stack
  }

  const resetSearchQueries = (): void => {
    telemetrySearchQuery.value = ''
    actionSearchQuery.value = ''
    configurationSearchQuery.value = ''
  }

  const resetAssetPanelState = (): void => {
    resetSearchQueries()
    configurationOpenActionKey.value = null
  }

  // Reset function for testing
  const $reset = (): void => {
    isLoading.value = false
    viewMode.value = 'data'
    showAllTelemetries.value = false
    resetAssetPanelState()
  }

  return {
    // State
    isLoading,
    viewMode,
    floatingCardMode,
    isMapFocused,
    listHeightLevel,
    isSearchBarVisible,
    openedSite,
    openedRoute,
    openedStack,
    stackType,
    showAllTelemetries,
    telemetrySearchQuery,
    actionSearchQuery,
    configurationSearchQuery,
    configurationOpenActionKey,

    // Actions
    setViewMode,
    setFloatingCardMode,
    setOpenedStack,
    resetSearchQueries,
    resetAssetPanelState,
    $reset
  }
})
